import {
  Text,
  Card,
  Group,
  Stack,
  Box,
  Avatar,
  Badge,
  SimpleGrid,
} from '@mantine/core';
import { MapPin, Clock } from 'react-feather';
import type { Coach } from '../../../types';
import { getCoachInitials, getCoachDisplayName } from '../../../utils/coachHelpers';
import { getAvatarColor } from '../../../utils/avatarHelpers';
import { Pagination } from '../../../components/common';
import { renderStatusPill, formatTimezone } from './CoachCellRenderers';
import classes from './CoachesGridView.module.css';

export interface CoachesGridViewProps {
  coaches: Coach[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onCoachClick: (coach: Coach) => void;
}

export function CoachesGridView({
  coaches,
  page,
  pageSize,
  totalCount,
  totalPages,
  onPageChange,
  onPageSizeChange,
  onCoachClick,
}: CoachesGridViewProps) {
  return (
    <>
      <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="lg">
        {coaches.map((coach) => (
          <Card
            key={coach.id}
            withBorder
            padding="lg"
            className={classes.gridCard}
            onClick={() => onCoachClick(coach)}
          >
            <Stack align="center" gap="sm">
              {(() => {
                const bgColor = getAvatarColor(`${coach.first_name || ''}${coach.last_name || ''}`);
                return (
                  <Avatar
                    size={80}
                    radius={80}
                    src={coach.avatar_url}
                    styles={{
                      root: { backgroundColor: bgColor },
                      placeholder: { color: '#FFFFFF', backgroundColor: bgColor },
                    }}
                  >
                    {getCoachInitials(coach)}
                  </Avatar>
                );
              })()}
              <Text fw={600} size="lg" ta="center" lineClamp={1}>
                {getCoachDisplayName(coach)}
              </Text>
              {coach.pronouns && (
                <Text size="sm" c="dimmed">
                  {coach.pronouns}
                </Text>
              )}
              <Group gap={6}>
                {renderStatusPill(coach)}
                {coach.coach_type === 'partner' && (
                  <Badge size="xs" variant="light" color="blue">
                    Partner
                  </Badge>
                )}
              </Group>

              {/* Location */}
              {coach.location && (
                <Group gap={4}>
                  <MapPin size={14} color="#888" />
                  <Text size="sm" c="dimmed">
                    {coach.location.split(',')[0]}
                  </Text>
                </Group>
              )}

              {/* Timezone */}
              <Group gap={4}>
                <Clock size={14} color="#888" />
                <Text size="sm" c="dimmed">
                  {formatTimezone(coach.home_base_timezone)}
                </Text>
              </Group>

              {/* Facilitator Pill */}
              {coach.facilitator_experience && (
                <Badge
                  size="sm"
                  style={{
                    backgroundColor: 'rgba(76, 175, 80, 0.12)',
                    color: '#2E7D32',
                    padding: '4px 10px',
                    borderRadius: '3px',
                    fontSize: '11px',
                    fontWeight: 500,
                  }}
                >
                  Facilitator
                </Badge>
              )}

              {/* Certifications */}
              {coach.certifications && coach.certifications.length > 0 && (
                <Text size="sm" c="dimmed" ta="center">
                  {coach.certifications.slice(0, 2).join(', ')}
                  {coach.certifications.length > 2 && ` +${coach.certifications.length - 2}`}
                </Text>
              )}

              {/* Expertise Areas */}
              {coach.expertise_areas && coach.expertise_areas.length > 0 && (
                <Text size="sm" c="dimmed" ta="center" lineClamp={1}>
                  {coach.expertise_areas.join(', ')}
                </Text>
              )}
            </Stack>

            {/* Stats Footer */}
            <Box className={classes.statsFooter}>
              <Group justify="center" gap="xl">
                <Box ta="center">
                  <Text fw={600} size="lg">
                    {coach.active_cohort_count}
                  </Text>
                  <Text size="xs" c="dimmed" tt="uppercase">
                    Cohorts
                  </Text>
                </Box>
                <Box ta="center">
                  <Text fw={600} size="lg">
                    —
                  </Text>
                  <Text size="xs" c="dimmed" tt="uppercase">
                    Sessions
                  </Text>
                </Box>
              </Group>
            </Box>
          </Card>
        ))}
      </SimpleGrid>

      {coaches.length === 0 && (
        <Card withBorder p="xl">
          <Text ta="center" c="dimmed">
            No coaches found matching your filters
          </Text>
        </Card>
      )}

      {totalCount > 0 && (
        <Card withBorder p={0} mt="md">
          <Pagination
            page={page}
            pageSize={pageSize}
            total={totalCount}
            totalPages={totalPages}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        </Card>
      )}
    </>
  );
}
